import React, { Fragment, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { NotificationManager } from 'react-notifications';
import TextInput from './TextInput';
import ReactRTE from './ReactRTE';
import Heading from './Heading';
import DropDown from './DropDown';
import { domains } from '../configs/domains';
import ArticleService from '../Services/ArticleService';
import StringUtils from '../Utils/StringUtils';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { Auth } from 'aws-amplify';
import GSCKwsReport from './GSCKwsReport';
import GSCChart from './GSCChart';

const typeOptions = [
	{
		key: 'article',
		value: 'article',
		label: 'article'
	},
	{
		key: 'category',
		value: 'category',
		label: 'category'
	}
];
const ALLOW_SLUG_EDIT = ['dewstarsforever@gmail.com', 'ahmed.abouhegaza@gmail.com'];

export default function EditArticle({ type, existingValues }) {
	const navigate = useNavigate();
	const [enable, setEnable] = useState(true);
	const [user, setUser] = useState(null);
	const [translationEnable, setTranslationEnable] = useState(true);

	const translate = (targetLanguage) => {
		setTranslationEnable(false);
		setEnable(false);
		ArticleService.translateArticle(existingValues.articleId, targetLanguage)
			.then(() => {
				setTranslationEnable(true);
				setEnable(true);
				window.location.reload(false);
			})
			.catch((e) => {
				alert(e);
			});
	};
	useEffect(() => {
		Auth.currentAuthenticatedUser().then((res) => {
			setUser(res.attributes.email);
		});

	}, []);
	let initialValues =
		type == 'CREATE'
			? createInitialValues
			: {
				...existingValues,
				tags: existingValues.tags ? StringUtils.arrayToTags(existingValues.tags) : null
			};
	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (values) => {
			if (type == 'CREATE') {
				ArticleService.createArticle(values)
					.then((response) => {
						NotificationManager.success(`Article Created`);
						navigate(`/update-article/${response}`);
					})
					.catch((e) => {
						NotificationManager.error(`Error Creating Article`);
						setEnable(true);
					});
			} else {
				ArticleService.updateArticle({
					...existingValues,
					...values,
					tags: values.tags ? StringUtils.tagsToArray(values.tags) : null
				})
					.then((response) => {
						NotificationManager.success(`Updated ${values.articleId}`);
						setEnable(true);
					})
					.catch((e) => {
						NotificationManager.error(`Error Updating ${values.articleId}`);
						setEnable(true);
					});
			}
		}
	});

	return (
		<Fragment>
			{existingValues && existingValues.redirect && <h1 className='text-center text-red-600'>PAGE IS REDIRECTED</h1>}
			{initialValues.parentArticleId && (
				<a href={`/update-article/${initialValues.parentArticleId}`}>Parent Article</a>
			)}
			{existingValues && <GSCChart
				domain={existingValues.domain}
				page={`https://www.${existingValues.domain}/article/${existingValues.slug}-${existingValues.articleId}`} />}
			{existingValues && <GSCKwsReport domain={existingValues.domain} page={existingValues.articleId} body={existingValues.body} />}
			<form>
				<TextInput
					label="Redirect"
					placeholder="Redirect url"
					name="redirect"
					onChange={formik.handleChange}
					value={formik.values.redirect}
					disabled={!enable || type === 'CREATE'}
					error={formik.errors.redirect}
					message={'You need a manager approval to redirect urls'}
				/>
				<DropDown
					onChange={(data) => {
						formik.setFieldValue('domain', data.value);
					}}
					value={formik.values.domain}
					name="domain"
					options={domains}
					label="Select domain"
					error={formik.errors.domain}
					disabled={type != 'CREATE'}
				/>
				<DropDown
					onChange={(data) => {
						formik.setFieldValue('type', data.value);
					}}
					value={formik.values.type}
					name="type"
					options={typeOptions}
					label="Type"
					error={formik.errors.type}
				/>
				<TextInput
					label="Slug"
					placeholder="Slug"
					name="slug"
					onChange={formik.handleChange}
					value={formik.values.slug}
					disabled={!enable || (type === 'UPDATE' && !ALLOW_SLUG_EDIT.includes(user))}
					error={formik.errors.slug}
					message={type != 'CREATE' ? 'You need a manager approval to change Slug' : null}
				/>
				<TextInput
					label="Category"
					placeholder="Category"
					name="category"
					onChange={formik.handleChange}
					value={formik.values.category}
					disabled={!enable}
					error={formik.errors.category}
				/>
				<TextInput
					label="Tags (tags are comma seperated, Ex: kayak, paddle, canoe) (one tag can have no commas: kayak)"
					placeholder="Tags"
					name="tags"
					onChange={formik.handleChange}
					value={formik.values.tags}
					disabled={!enable}
					error={formik.errors.tags}
					gpt={"ARTICLE_TAGS"}
					setFieldValue={formik.setFieldValue}
					gptOthers={formik.values.body}
				/>
				<TextInput
					label="Title"
					placeholder="Title"
					name="title"
					onChange={formik.handleChange}
					value={formik.values.title}
					disabled={!enable}
					error={formik.errors.title}
					gpt={"ARTICLE_TITLE"}
					setFieldValue={formik.setFieldValue}
				/>
				<TextInput
					label="SEO Title"
					placeholder="SEO Title (Max 60 characters)"
					name="seoTitle"
					onChange={formik.handleChange}
					value={formik.values.seoTitle}
					disabled={!enable}
					error={formik.errors.seoTitle}
					gpt={"ARTICLE_SEO_TITLE"}
					setFieldValue={formik.setFieldValue}
					gptOthers={formik.values.featuredBullets}
				/>
				<TextInput
					label="SEO Description (Max 160 characters)"
					placeholder="SEO Description (Max 160 characters)"
					name="seoDescription"
					onChange={formik.handleChange}
					value={formik.values.seoDescription}
					disabled={!enable}
					error={formik.errors.seoDescription}
					gpt={"ARTICLE_SEO_DESCRIPTION"}
					setFieldValue={formik.setFieldValue}
					gptOthers={formik.values.featuredBullets}
				/>
				<TextInput
					label="Image (Image URL ex:https://www.mywebsite.com/image.png)"
					placeholder="Image url"
					name="image"
					onChange={formik.handleChange}
					value={formik.values.image}
					disabled={!enable}
					error={formik.errors.image}
				/>
				<TextInput
					label="Image description (ex: four women smilling, table with food, red airplane,....etc)"
					placeholder="Image Alt"
					name="imageAlt"
					onChange={formik.handleChange}
					value={formik.values.imageAlt}
					disabled={!enable}
					error={formik.errors.imageAlt}
				/>
				<label className="w-full form-label font-bold inline-block mt-2 text-gray-700">Breadcrumbs</label>
				<table className="table-auto w-4/5">
					<tr>
						<td>Text</td>
						<td>Link</td>
					</tr>
					<tr>
						<td style={{ width: '30%' }}>
							<TextInput
								placeholder="Breadcrumb 1 Text"
								name="breadcrumb_text_1"
								onChange={formik.handleChange}
								value={formik.values.breadcrumb_text_1}
								disabled={!enable}
								error={formik.errors.breadcrumb_text_1}
								noMargin={true}
							/>
						</td>
						<td style={{ width: '70%' }}>
							<TextInput
								placeholder="Link 1"
								name="breadcrumb_link_1"
								onChange={formik.handleChange}
								value={formik.values.breadcrumb_link_1}
								disabled={!enable}
								error={formik.errors.breadcrumb_link_1}
								noMargin={true}
							/>
						</td>
					</tr>
					<tr>
						<td style={{ width: '30%' }}>
							<TextInput
								placeholder="Breadcrumb 2 Text"
								name="breadcrumb_text_2"
								onChange={formik.handleChange}
								value={formik.values.breadcrumb_text_2}
								disabled={!enable}
								error={formik.errors.breadcrumb_text_2}
								noMargin={true}
							/>
						</td>
						<td style={{ width: '70%' }}>
							<TextInput
								placeholder="Link 2"
								name="breadcrumb_link_2"
								onChange={formik.handleChange}
								value={formik.values.breadcrumb_link_2}
								disabled={!enable}
								error={formik.errors.breadcrumb_link_2}
								noMargin={true}
							/>
						</td>
					</tr>
				</table>
				<label className="w-full form-label font-bold inline-block mt-2 text-gray-700">Body</label>
				<ReactRTE
					value={formik.values.body}
					setValue={formik.setFieldValue}
					name="body"
					id="body"
					gpt={"ARTICLE_BODY"}
					gptOthers={formik.values.body}
				/>
				<Button text="Submit" disabled={!enable} type="submit" onClick={formik.handleSubmit} />
			</form>


			{type !== 'CREATE' &&
				(!existingValues.language || existingValues.language === 'en') && (
					<Fragment>
						<hr />
						<Heading type="h1" text="Translation" />
						<table className="table-auto max-w-full">
							<tbody>
								<tr className="border-2">
									<td>Spanish </td>
									<td>
										{existingValues.languages && existingValues.languages['es'] ? (
											links(existingValues, 'es')
										) : (
											<Button
												text="Create"
												disabled={!translationEnable}
												onClick={() => {
													translate('es');
												}}
											/>
										)}
									</td>
								</tr>
								<tr className="border-2">
									<td>Portugeese </td>
									<td>
										{existingValues.languages && existingValues.languages['pt'] ? (
											links(existingValues, 'pt')
										) : (
											<Button
												text="Create"
												disabled={!translationEnable}
												onClick={() => {
													translate('pt');
												}}
											/>
										)}
									</td>
								</tr>
								<tr className="border-2">
									<td>French </td>
									<td>
										{existingValues.languages && existingValues.languages['fr'] ? (
											links(existingValues, 'fr')
										) : (
											<Button
												text="Create"
												disabled={!translationEnable}
												onClick={() => {
													translate('fr');
												}}
											/>
										)}
									</td>
								</tr>
								<tr className="border-2">
									<td>German </td>
									<td>
										{existingValues.languages && existingValues.languages['de'] ? (
											links(existingValues, 'de')
										) : (
											<Button
												text="Create"
												disabled={!translationEnable}
												onClick={() => {
													translate('de');
												}}
											/>
										)}
									</td>
								</tr>
							</tbody>
						</table>
					</Fragment>
				)}
		</Fragment>
	);
}

const validationSchema = Yup.object().shape({
	domain: Yup.string().required('Required'),
	redirect: Yup.string().url().nullable(),
	type: Yup.string().required('Required'),
	category: Yup.string().nullable(),
	title: Yup.string()
		.min(3, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 60')
		.required('Required'),
	seoTitle: Yup.string()
		.min(3, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 60')
		.required('Required'),
	tags: Yup.string()
		.min(1, 'Too Short! It should be more than 3')
		.max(400, 'Too Long! It should be less than 400')
		.nullable(),
	seoDescription: Yup.string()
		.min(3, 'Too Short! It should be more than 3')
		.max(160, 'Too Long! It should be less than 160')
		.required('Required'),
	slug: Yup.string()
		.min(3, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 160')
		.matches(/^[a-z0-9_.-]+$/, 'Lower case characters, numbers and dashes only. So white spaces!')
		.required('Required'),
	imageAlt: Yup.string()
		.min(3, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 160')
		.required('Required'),
	image: Yup.string().url().required('Required'),
	body: Yup.string().min(3, 'Too Short! It should be more than 3').required('Required'),
	breadcrumb_text_1: Yup.string()
		.min(2, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 160')
		.nullable(),
	breadcrumb_text_2: Yup.string()
		.min(2, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 160')
		.nullable(),
	breadcrumb_link_1: Yup.string().nullable().url(),
	breadcrumb_link_2: Yup.string().nullable().url()
});

const createInitialValues = {
	redirect: null,
	title: '',
	seoTitle: '',
	type: 'article',
	seoDescription: '',
	slug: '',
	image: '',
	imageAlt: '',
	domain: '',
	body: '',
	category: null,
	breadcrumb_text_1: null,
	breadcrumb_link_1: null,
	breadcrumb_text_2: null,
	breadcrumb_link_2: null
};

const links = (existingValues, lang) => (
	<Fragment>
		<a href={`/update-article/${existingValues.languages[lang]}`} target="_blank" style={{ marginRight: '15px' }}>
			Edit
		</a>
		<a href={`https://${existingValues.domain}/article/${existingValues.languages[lang]}`} target="_blank">
			live
		</a>
	</Fragment>
);
